import { useMemo } from "react";
import { ProjectPlanSort } from "../models/project-plan-store";
import {
    PlanSegment,
    ProjectParticipant,
    ProjectPlanDetail,
    ProjectPlanProject,
    ProjectPlanSectionView,
} from "../models/project-plan-types";

/**
 * Проекты, готовые к отрисовке: отфильтрованные поиском и обрезанные по видимой сетке.
 *
 * Поиск двухуровневый. Если запрос попал в сам проект (номер, название, номер
 * договора, ГИП) — показываем его целиком. Если только в участников — оставляем в проекте
 * лишь совпавшие строки: искали человека, значит и раскрыть проект надо на нём,
 * а не на всём отделе, который рядом с ним работает.
 *
 * При этом заголовок проекта фильтр не режет: сводная полоса и крайний день
 * приходят с сервера, посчитанные по ВСЕМ участникам — в том числе по тем, кого
 * не показывают права на отдел. Срок проекта — свойство проекта, а не выборки,
 * и по нему должно быть видно, где внутри него лежат найденные работы.
 *
 * Обрезка по сетке обязательна. Отрезки собираются по ВСЕМ загруженным колонкам
 * (с сервера приходит максимум обоих масштабов), а сетка рисует только первые
 * `columnsCount` из них. Индекс за этой границей `columnLeft` отдаёт как правый край
 * последней колонки — полоса схлопнулась бы в артефакт у края вместо того, чтобы исчезнуть.
 *
 * В режиме «Разделы» то же правило на уровень ниже: совпал раздел (шифр, описание,
 * ответственный) — показываем его целиком, совпали только исполнители — раздел раскрыт
 * на них. Проект остаётся, если в нём остался хоть один раздел.
 *
 * Порядок задаёт `sort`: по номеру проекта (новые сверху) или по ГИПу — ФИО по алфавиту,
 * внутри одного ГИПа снова по убыванию номера, проекты без ГИПа в конце.
 *
 * Считается один раз на изменение данных, поиска, порядка или глубины показа — в рендере
 * полос остаётся только чтение готовых чисел.
 */
export function useVisibleProjects(
    projects: ProjectPlanProject[],
    query: string,
    columnsCount: number,
    sort: ProjectPlanSort,
    detail: ProjectPlanDetail
): ProjectPlanProject[] {
    return useMemo(() => {
        const result: ProjectPlanProject[] = [];

        projects.forEach((project) => {
            const wholeProject = !query || matchesProject(project, query);
            const summary = project.summary ? clipAll([project.summary], columnsCount) : [];

            if (detail === "sections") {
                const sections: ProjectPlanSectionView[] = [];

                project.sections.forEach((section) => {
                    const wholeSection = wholeProject || matchesSection(section, query);
                    const rows = visibleRows(section.rows, wholeSection, query, columnsCount);
                    const bar = section.summary ? clipAll([section.summary], columnsCount) : [];

                    // раздел без полосы на сетке и без исполнителей показывать незачем
                    if ((wholeSection && bar.length) || rows.length) {
                        sections.push({ ...section, rows, summary: bar[0] || null });
                    }
                });

                if (sections.length) {
                    result.push({ ...project, sections, summary: summary[0] || null });
                }

                return;
            }

            const shown = visibleRows(project.rows, wholeProject, query, columnsCount);

            if (!shown.length) {
                return;
            }

            result.push({ ...project, rows: shown, summary: summary[0] || null });
        });

        return result.sort(sort === "gip" ? byGip : byNumber);
    }, [projects, query, columnsCount, sort, detail]);
}

/** Строки участников: совпавшие с поиском (или все) и обрезанные по видимой сетке */
function visibleRows(
    rows: ProjectParticipant[],
    all: boolean,
    query: string,
    columnsCount: number
): ProjectParticipant[] {
    const shown: ProjectParticipant[] = [];

    rows.forEach((row) => {
        if (!all && !matchesRow(row, query)) {
            return;
        }

        const segments = clipAll(row.segments, columnsCount);

        if (segments.length) {
            shown.push({ ...row, segments });
        }
    });

    return shown;
}

function matchesSection(section: ProjectPlanSectionView, query: string): boolean {
    return `${section.name} ${section.code} ${section.description} ${section.note}`.toLowerCase().indexOf(query) >= 0;
}

function byNumber(left: ProjectPlanProject, right: ProjectPlanProject): number {
    return right.inventoryNumber - left.inventoryNumber;
}

function byGip(left: ProjectPlanProject, right: ProjectPlanProject): number {
    const a = (left.employer || "").trim();
    const b = (right.employer || "").trim();

    // проекты без ГИПа — в конец списка
    if (!a !== !b) {
        return a ? -1 : 1;
    }

    return a.localeCompare(b, "ru", { sensitivity: "base" }) || byNumber(left, right);
}

function matchesProject(project: ProjectPlanProject, query: string): boolean {
    // по ГИПу ищут так же часто, как по номеру: «что сейчас у Иванова»
    const haystack = `${project.fullInventoryNumber} ${project.name} ${project.orderNumber} ${project.employer}`.toLowerCase();

    return haystack.indexOf(query) >= 0;
}

function matchesRow(row: ProjectParticipant, query: string): boolean {
    return `${row.label} ${row.note}`.toLowerCase().indexOf(query) >= 0;
}

function clipAll(segments: PlanSegment[], columnsCount: number): PlanSegment[] {
    const result: PlanSegment[] = [];

    segments.forEach((segment) => {
        const last = segment.start + segment.length - 1;
        const end = Math.min(last, columnsCount - 1);

        if (segment.start <= end) {
            result.push({
                ...segment,
                length: end - segment.start + 1,
                // сетка обрезала отрезок сильнее, чем бэк — полоса всё равно уходит за край
                continuesRight: segment.continuesRight || end < last,
            });
        }
    });

    return result;
}
